import React, { useState } from 'react';
import { TrendingUpIcon, UsersIcon, MailOpenIcon, MessageSquareIcon, PhoneOffIcon, StarIcon, MapPinIcon, CheckCircle2Icon, ArrowRightIcon, SparklesIcon, BarChart3Icon, FilterIcon, XIcon, SendIcon, MousePointerClickIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getCategoryLabel } from '../data/communications';
import type { ChannelKey, Notice } from '../types/notice';
const CHANNEL_LABELS: Record<ChannelKey, {
  fr: string;
  en: string;
}> = {
  email: {
    fr: 'Courriel',
    en: 'Email'
  },
  sms: {
    fr: 'SMS',
    en: 'SMS'
  },
  voice: {
    fr: 'Voix automatisée',
    en: 'Automated voice'
  },
  push: {
    fr: 'Notification Push',
    en: 'Push notification'
  },
  website: {
    fr: 'Site web',
    en: 'Website'
  },
  social: {
    fr: 'Réseaux sociaux',
    en: 'Social media'
  }
};
interface AnalyticsDashboardProps {
  /** When set, the dashboard reports on this single notice. */
  focusedNotice?: Notice | null;
  onClearFocus?: () => void;
}

/** Compact metric tile shared by both dashboard modes. */
function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
  hintTone = 'muted',
  pending
}: {
  icon: typeof TrendingUpIcon;
  label: string;
  value: string;
  hint?: string;
  hintTone?: 'muted' | 'positive';
  /** Replaces the figure with a "available once sent" placeholder. */
  pending?: string;
}) {
  return <div className={`p-5 rounded-xl shadow-sm border ${pending ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-100'}`} data-id="element-34" data-mp="1j6ol07pi5w4v">
      <div className="flex items-center gap-3 mb-2" data-id="element-35" data-mp="1o7cklhrsrlap">
        <div className={`p-2 rounded-lg ${pending ? 'bg-slate-200 text-slate-400' : 'bg-indigo-50 text-[#4F46E5]'}`} data-id="element-36" data-mp="1a6iamup3pc9x">
          <Icon className="w-5 h-5" data-id="element-37" data-mp="1e9oexwsnktdr" />
        </div>
        <h3 className="text-sm font-medium text-slate-600" data-id="element-38" data-mp="34jroeyoojjxz">{label}</h3>
      </div>
      {pending ? <p className="text-sm text-slate-400 italic pt-1.5" data-id="element-39" data-mp="38klbjpuasupt">{pending}</p> : <div className="flex items-end gap-3 flex-wrap" data-id="element-40" data-mp="ldoz75gg4ec5">
          <span className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-41" data-mp="lb2r94pn7wte">
            {value}
          </span>
          {hint && <span className={`text-sm mb-1 ${hintTone === 'positive' ? 'font-medium text-emerald-600' : 'text-slate-500'}`} data-id="element-42" data-mp="cbz5fsz3d3b7">
              {hint}
            </span>}
        </div>}
    </div>;
}
export function AnalyticsDashboard({
  focusedNotice = null,
  onClearFocus
}: AnalyticsDashboardProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [selectedSector, setSelectedSector] = useState('A');
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';

  // ---------------------------------------------------------------- FOCUSED
  if (focusedNotice) {
    const stats = focusedNotice.stats;
    // Not sent yet: the layout still shows, with each figure marked as pending.
    const pending = stats ? undefined : t('Disponible après l’envoi', 'Available once sent');
    return <div className="space-y-8 animate-in fade-in duration-300" data-id="element-43" data-mp="1vancytbjlzwy">
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-id="element-44" data-mp="1vd033trgfgl3">
          <div className="min-w-0" data-id="element-45" data-mp="24c3k8s729lwy">
            <div className="flex items-center gap-2 flex-wrap mb-1.5" data-id="element-46" data-mp="24d6dv7vrq2gk">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white text-[#4F46E5] text-xs font-semibold border border-indigo-200" data-id="element-47" data-mp="299wp9hwmo9vn">
                <FilterIcon className="w-3 h-3" data-id="element-48" data-mp="2j0zt2rgxz2ym" />
                {t('Filtré sur un avis', 'Filtered on one notice')}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-white text-slate-600 text-xs font-medium border border-slate-200" data-id="element-49" data-mp="2nyj6jp5tfbpi">
                {getCategoryLabel(focusedNotice.category)[lang]}
              </span>
              <span className="text-xs text-slate-500" data-id="element-50" data-mp="2x1253x2kj25y">{focusedNotice.id}</span>
            </div>
            <h2 className="text-lg font-bold text-[#0F172A] font-heading truncate" data-id="element-51" data-mp="2jc8ua1h2ubjc">
              {focusedNotice.title}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-52" data-mp="2t3lnawem37qy">
              {focusedNotice.status === 'scheduled' ? t('Envoi prévu le ', 'Sending on ') : focusedNotice.status === 'draft' ? t('Brouillon modifié le ', 'Draft edited on ') : t('Envoyé le ', 'Sent on ')}
              {new Date(focusedNotice.date).toLocaleDateString(locale, {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
              {focusedNotice.audience.detail ? ` · ${focusedNotice.audience.detail[lang]}` : ''}
            </p>
          </div>
          <button onClick={onClearFocus} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-53" data-mp="3ludfzjwgxe9j">
            <XIcon className="w-4 h-4" data-id="element-54" data-mp="17krlx4tupnaa" />
            {t('Voir tout le module', 'View the whole module')}
          </button>
        </div>

        {pending && <p className="flex items-start gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-id="element-55" data-mp="yjtioor8ljsr">
            <BarChart3Icon className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" data-id="element-56" data-mp="1m95orfr9pbqu" />
            {t('Cet avis n’a pas encore été diffusé. L’audience et les canaux sont déjà connus ; les mesures de performance apparaîtront après l’envoi.', 'This notice has not gone out yet. Its audience and channels are already known; performance figures will appear once sent.')}
          </p>}

        <>
            <section data-id="element-57" data-mp="256ypqcibzbll">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4 flex items-center gap-2" data-id="element-58" data-mp="29z001svxus8z">
                <BarChart3Icon className="w-5 h-5 text-[#4F46E5]" data-id="element-59" data-mp="2jup28od5slcc" />
                {t('Performance de cet avis', 'Performance of this notice')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-60" data-mp="2ewnie17wshzt">
                <MetricCard icon={SendIcon} label={t('Destinataires', 'Recipients')} value={stats ? stats.recipients.toLocaleString(locale) : focusedNotice.audience.count.toLocaleString(locale)} hint={stats ? `${stats.delivered.toLocaleString(locale)} ${t('livrés', 'delivered')}` : t('audience ciblée', 'targeted audience')} data-id="element-61" data-mp="29v7xqa9n8dhy" />
                <MetricCard icon={MailOpenIcon} label={t("Taux d'ouverture", 'Open rate')} value={stats ? `${stats.openRate} %` : '—'} hint={t('tous canaux confondus', 'across all channels')} pending={pending} data-id="element-62" data-mp="1h72ddomi4qin" />
                <MetricCard icon={MousePointerClickIcon} label={t('Taux de clics', 'Click rate')} value={stats ? `${stats.clickRate} %` : '—'} hint={t('sur les liens de l’avis', 'on the notice links')} pending={pending} data-id="element-63" data-mp="3stw4v9i6n836" />
                <MetricCard icon={MessageSquareIcon} label={t('Interactions citoyennes', 'Citizen interactions')} value={stats ? stats.interactions.toLocaleString(locale) : '—'} hint={t('clics, réponses, questions', 'clicks, replies, questions')} pending={pending} data-id="element-64" data-mp="3541tkwdihrl1" />
                <MetricCard icon={PhoneOffIcon} label={t('Appels évités', 'Calls avoided')} value={stats ? stats.callsAvoided.toLocaleString(locale) : '—'} hint={t('estimé par ATNA', 'estimated by ATNA')} pending={pending} data-id="element-65" data-mp="2meverkfu9k4i" />
                <MetricCard icon={StarIcon} label={t('Satisfaction', 'Satisfaction')} value={stats ? `${stats.satisfaction.toLocaleString(locale)} / 5` : '—'} hint={t('sur cet avis', 'for this notice')} pending={pending} data-id="element-66" data-mp="1tru4qt44yc35" />
              </div>
            </section>

            <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-67" data-mp="1yqoktnapxmjk">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-68" data-mp="2chtqp74ojb2c">
                {t('Performance par canal', 'Performance by channel')}
              </h3>
              <div className="overflow-x-auto" data-id="element-69" data-mp="2mfpvlh70x9s0">
                <table className="w-full text-sm text-left" data-id="element-70" data-mp="2mees7ihhq41f">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-71" data-mp="2hir5raue7ozm">
                    <tr data-id="element-72" data-mp="312yg58m82rgn">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-73" data-mp="313repmb12anp">
                        {t('Canal', 'Channel')}
                      </th>
                      <th className="px-4 py-3" data-id="element-74" data-mp="2r526kl8xo4kn">
                        {t('Messages envoyés', 'Messages sent')}
                      </th>
                      <th className="px-4 py-3" data-id="element-75" data-mp="2w33ov3yjbzv2">
                        {t("Taux d'ouverture", 'Open rate')}
                      </th>
                      <th className="px-4 py-3 rounded-r-lg" data-id="element-76" data-mp="2ianh5puovdnt">
                        {t('Taux de clics', 'Click rate')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-77" data-mp="28gdl9y6jfx8c">
                    {stats ? stats.channels.map(c => <tr key={c.channel} data-id="element-78" data-mp="1zdm9tydnx1gp">
                            <td className="px-4 py-3 font-medium text-slate-900" data-id="element-79" data-mp="1zenumukhd2bm">
                              {CHANNEL_LABELS[c.channel][lang]}
                            </td>
                            <td className="px-4 py-3 text-slate-600" data-id="element-80" data-mp="2n8if279fcxg0">
                              {c.sent > 0 ? c.sent.toLocaleString(locale) : '—'}
                            </td>
                            <td className="px-4 py-3" data-id="element-81" data-mp="2e9nat0pxxxfc">
                              <div className="flex items-center gap-2" data-id="element-82" data-mp="2ia78c5eff320">
                                <span className="w-10" data-id="element-83" data-mp="2iagz3v7ape8r">{c.openRate} %</span>
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-84" data-mp="3612x0tnl5i51">
                                  <div className="h-full bg-[#4F46E5]" style={{
                          width: `${c.openRate}%`
                        }} data-id="element-85" data-mp="3axjljoyp5yin" />
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3" data-id="element-86" data-mp="3oxck26ggtj16">
                              <div className="flex items-center gap-2" data-id="element-87" data-mp="3tpm3r4qzlmv0">
                                <span className="w-10" data-id="element-88" data-mp="1iadhwkowvizl">{c.clickRate} %</span>
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-89" data-mp="1i8agz5m805g3">
                                  <div className="h-full bg-emerald-500" style={{
                          width: `${c.clickRate}%`
                        }} data-id="element-90" data-mp="1d97o0jjilpoh" />
                                </div>
                              </div>
                            </td>
                          </tr>) : focusedNotice.channels.map(c => <tr key={c} data-id="element-91" data-mp="19a8cgg3a2uo2">
                            <td className="px-4 py-3 font-medium text-slate-900" data-id="element-92" data-mp="19b9vp7ug6ngv">
                              {CHANNEL_LABELS[c][lang]}
                            </td>
                            <td className="px-4 py-3 text-slate-400 italic" colSpan={3} data-id="element-93" data-mp="2knswarqgiypn">
                              {pending}
                            </td>
                          </tr>)}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-94" data-mp="2wqvipfqbd6dk">
              <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-95" data-mp="31qrnhh6u51u6">
                {t('Audience ciblée', 'Targeted audience')}
              </h3>
              <div className="flex items-center gap-3 flex-wrap" data-id="element-96" data-mp="240da5z6tmcsr">
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-97" data-mp="292ccr6uql5on">
                  <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-98" data-mp="2irdr6slmvf0i" />
                  {focusedNotice.audience.detail?.[lang] ?? t("Toute l'organisation", 'Entire organization')}
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-99" data-mp="1qajg92ggl2c6">
                  <UsersIcon className="w-4 h-4 text-slate-400" data-id="element-100" data-mp="7f5k8ia0mluf" />
                  {focusedNotice.audience.count.toLocaleString(locale)}{' '}
                  {t('citoyens', 'citizens')}
                </span>
                {focusedNotice.channels.map(c => <span key={c} className="px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100 text-sm text-[#4F46E5] font-medium" data-id="element-101" data-mp="3pki8p7qiu6rk">
                    {CHANNEL_LABELS[c][lang]}
                  </span>)}
              </div>
            </section>
        </>
      </div>;
  }

  // ----------------------------------------------------------------- GLOBAL
  const sectors = [{
    id: 'A',
    status: 'green',
    citizens: 12450,
    cards: 9820,
    openRate: 78,
    notices: 45,
    questions: 342
  }, {
    id: 'B',
    status: 'yellow',
    citizens: 8210,
    cards: 5100,
    openRate: 62,
    notices: 38,
    questions: 512
  }, {
    id: 'C',
    status: 'green',
    citizens: 15300,
    cards: 13900,
    openRate: 85,
    notices: 52,
    questions: 215
  }, {
    id: 'D',
    status: 'red',
    citizens: 6800,
    cards: 2850,
    openRate: 45,
    notices: 25,
    questions: 890
  }, {
    id: 'E',
    status: 'yellow',
    citizens: 9100,
    cards: 5200,
    openRate: 55,
    notices: 30,
    questions: 420
  }];
  const sectorName = (id: string) => `${t('Secteur', 'District')} ${id}`;
  const active = sectors.find(s => s.id === selectedSector) ?? sectors[0];
  const channelRows: {
    channel: ChannelKey | 'atna';
    sent: string;
    open: number;
    interact: number;
    best?: boolean;
  }[] = [{
    channel: 'push',
    sent: '85,420',
    open: 88,
    interact: 24,
    best: true
  }, {
    channel: 'sms',
    sent: '42,100',
    open: 92,
    interact: 18
  }, {
    channel: 'email',
    sent: '112,500',
    open: 45,
    interact: 8
  }, {
    channel: 'voice',
    sent: '8,400',
    open: 75,
    interact: 5
  }, {
    channel: 'atna',
    sent: '15,200',
    open: 100,
    interact: 42
  }, {
    channel: 'website',
    sent: '—',
    open: 35,
    interact: 12
  }, {
    channel: 'social',
    sent: '—',
    open: 22,
    interact: 6
  }];
  const funnel = [{
    label: t('Avis créés', 'Notices created'),
    value: '1,245',
    percent: 100,
    color: 'bg-slate-800'
  }, {
    label: t('Avis envoyés', 'Notices sent'),
    value: '125,842',
    percent: 95,
    color: 'bg-[#4F46E5]'
  }, {
    label: t('Ouverts', 'Opened'),
    value: '91,864',
    percent: 73,
    color: 'bg-indigo-400'
  }, {
    label: t('Cliqués', 'Clicked'),
    value: '34,210',
    percent: 27,
    color: 'bg-indigo-300'
  }, {
    label: t('Questions posées', 'Questions asked'),
    value: '8,450',
    percent: 6.7,
    color: 'bg-emerald-400'
  }, {
    label: t('Actions complétées', 'Actions completed'),
    value: '5,120',
    percent: 4.1,
    color: 'bg-emerald-500'
  }];
  const topics = [{
    topic: t('Déneigement', 'Snow removal'),
    sent: '45k',
    open: '88%',
    click: '34%',
    q: 1240
  }, {
    topic: t('Collecte des déchets', 'Waste collection'),
    sent: '112k',
    open: '75%',
    click: '12%',
    q: 890
  }, {
    topic: t('Travaux routiers', 'Road works'),
    sent: '28k',
    open: '82%',
    click: '41%',
    q: 2100
  }, {
    topic: t("Restrictions d'eau", 'Water restrictions'),
    sent: '15k',
    open: '91%',
    click: '22%',
    q: 450
  }, {
    topic: t('Taxes', 'Taxes'),
    sent: '85k',
    open: '65%',
    click: '18%',
    q: 1850
  }, {
    topic: t('Permis', 'Permits'),
    sent: '12k',
    open: '58%',
    click: '25%',
    q: 920
  }, {
    topic: t('Loisirs', 'Recreation'),
    sent: '65k',
    open: '42%',
    click: '15%',
    q: 310
  }, {
    topic: t('Bibliothèques', 'Libraries'),
    sent: '32k',
    open: '38%',
    click: '8%',
    q: 120
  }];
  const questions = [t('Quand est ma prochaine collecte ?', 'When is my next collection?'), t('Fermetures de routes', 'Road closures'), t('Règles de stationnement', 'Parking rules'), t('Taxes municipales', 'Municipal taxes'), t('Demande de permis', 'Permit application'), t('Piscines publiques', 'Public pools'), t('Services de bibliothèque', 'Library services'), t('Gestion des matières résiduelles', 'Waste management'), t("Avis d'urgence", 'Emergency notices'), t('Programmes de loisirs', 'Recreation programs')];
  return <div className="space-y-8 animate-in fade-in duration-300" data-id="element-102" data-mp="25yoaw9l2xujn">
      {/* Executive summary */}
      <section data-id="element-103" data-mp="2140t0tkkzye1">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap" data-id="element-104" data-mp="1w2v3f4m7wr8q">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-105" data-mp="1w4oa2jwtzbgq">
            <BarChart3Icon className="w-5 h-5 text-[#4F46E5]" data-id="element-106" data-mp="1s3ce7wnd5u03" />
            {t('Sommaire exécutif', 'Executive summary')}
          </h2>
          <div className="flex items-center gap-2 text-sm text-slate-500" data-id="element-107" data-mp="2jrxiq5tdq802">
            <FilterIcon className="w-4 h-4" data-id="element-108" data-mp="2f1phebduxnnu" />
            <span data-id="element-109" data-mp="2f0edmbkewfwq">{t('30 derniers jours', 'Last 30 days')}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-110" data-mp="2az2m20hz9klw">
          <MetricCard icon={TrendingUpIcon} label={t('Avis envoyés', 'Notices sent')} value="125,842" hint={t('+18 % vs mois préc.', '+18% vs. prev. month')} hintTone="positive" data-id="element-111" data-mp="37ijpzuvk7o90" />
          <MetricCard icon={UsersIcon} label={t('Citoyens atteints', 'Citizens reached')} value="91 %" hint={t('des citoyens inscrits', 'of registered citizens')} data-id="element-112" data-mp="3me0xbwmy7k8n" />
          <MetricCard icon={MailOpenIcon} label={t("Taux d'ouverture", 'Open rate')} value="73 %" hint={t('tous canaux confondus', 'across all channels')} data-id="element-113" data-mp="3hg8tc49147o3" />
          <MetricCard icon={MessageSquareIcon} label={t('Interactions citoyennes', 'Citizen interactions')} value="12,487" hint={t('clics, réponses, questions', 'clicks, replies, questions')} data-id="element-114" data-mp="dz8qpixwcp7q" />
          <MetricCard icon={PhoneOffIcon} label={t('Appels évités', 'Calls avoided')} value="2,134" hint={t('estimé par ATNA', 'estimated by ATNA')} data-id="element-115" data-mp="3zrq3fxi51im" />
          <MetricCard icon={StarIcon} label={t('Satisfaction', 'Satisfaction')} value="4.7 / 5" hint={t('communications', 'communications')} data-id="element-116" data-mp="2osv1ulo3o4bd" />
        </div>
      </section>

      {/* Recommendations & value */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-117" data-mp="1ftlicva1qa7d">
        <section className="lg:col-span-2" data-id="element-118" data-mp="1btb4yxjxchih">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 h-full relative overflow-hidden" data-id="element-119" data-mp="1bucpdsmtygcv">
            <div className="absolute top-0 right-0 p-6 opacity-10" data-id="element-120" data-mp="o3p7cej7aacl">
              <SparklesIcon className="w-32 h-32 text-[#4F46E5]" data-id="element-121" data-mp="j5fbe1pnj5px" />
            </div>
            <div className="relative z-10" data-id="element-122" data-mp="wwajvcw8nksz">
              <div className="flex items-center gap-2 mb-4" data-id="element-123" data-mp="wx28u4vbykaa">
                <SparklesIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-124" data-mp="s4siwou7tk80" />
                <h2 className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-125" data-mp="4fgd7yy3juag">
                  {t('Recommandations ATNA', 'ATNA recommendations')}
                </h2>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-5 border border-indigo-100/50 mb-5" data-id="element-126" data-mp="c1w14yf97vf">
                <p className="text-slate-700 font-medium mb-1" data-id="element-127" data-mp="2mjhr1mx0kunh">
                  {t('Le Secteur A montre une forte portée numérique (83 %).', 'District A shows strong digital reach (83%).')}
                </p>
                <p className="text-slate-600 text-sm mb-4" data-id="element-128" data-mp="2vi33d0bw7dft">
                  {t("Les requêtes concernant l'entretien routier sont en hausse dans cette zone.", 'Requests about road maintenance are rising in this area.')}
                </p>

                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-129" data-mp="3j7nxmfdhxx4e">
                  {t('Action recommandée', 'Recommended action')}
                </h4>
                <ul className="space-y-2 mb-4" data-id="element-130" data-mp="1ow955qt3pjky">
                  {[t('Envoyer un avis ciblé', 'Send a targeted notice'), t('Utiliser Push + SMS', 'Use Push + SMS'), t('Notifier uniquement le secteur affecté', 'Notify only the affected district')].map(item => <li key={item} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-131" data-mp="3mplf1wfnoyi3">
                      <CheckCircle2Icon className="w-4 h-4 text-emerald-500" data-id="element-132" data-mp="3csgpdmmlqkwj" />
                      {item}
                    </li>)}
                </ul>

                <div className="flex items-center gap-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-133" data-mp="33tbtyp6c77ol">
                  <div className="flex-1" data-id="element-134" data-mp="37tnem1yp9802">
                    <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1" data-id="element-135" data-mp="37whwblqbweps">
                      {t('Impact prévu', 'Expected impact')}
                    </div>
                    <div className="flex gap-4 text-sm font-medium text-emerald-700 flex-wrap" data-id="element-136" data-mp="16296qa1ixen8">
                      <span data-id="element-137" data-mp="2f1acy7fjm1fi">
                        ↓ 22 % {t("d'appels entrants", 'inbound calls')}
                      </span>
                      <span data-id="element-138" data-mp="25zavje0m8vc1">↑ 18 % {t('de satisfaction', 'satisfaction')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <button className="flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-5 py-2.5 rounded-lg font-medium transition-colors shadow-sm shadow-indigo-200" data-id="element-139" data-mp="2122bf7vwqh85">
                {t("Préparer l'avis citoyen", 'Prepare citizen notice')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-140" data-mp="2oxddj8uqxs0c" />
              </button>
            </div>
          </div>
        </section>

        <section className="lg:col-span-1" data-id="element-141" data-mp="2jyajskk7v87o">
          <div className="bg-[#0F172A] rounded-xl p-6 h-full text-white shadow-lg relative overflow-hidden" data-id="element-142" data-mp="3hehbqetkzxqo">
            <h2 className="text-xl font-bold font-heading mb-6" data-id="element-143" data-mp="3co0yauprgc4b">
              {t('Valeur générée par bciti+', 'Value generated by bciti+')}
            </h2>

            <div className="space-y-5 relative z-10" data-id="element-144" data-mp="3k0qenw1bsro3">
              <div data-id="element-145" data-mp="3ox73ksgcna28">
                <div className="text-3xl font-bold text-[#818CF8] mb-1" data-id="element-146" data-mp="3oyrxqgyr4qzk">
                  87 000 $
                </div>
                <div className="text-sm text-slate-300" data-id="element-147" data-mp="3tvs64yj2q5tu">
                  {t('Économies opérationnelles estimées', 'Estimated operational savings')}
                </div>
              </div>

              <div className="h-px bg-slate-700/50" data-id="element-148" data-mp="bqxcd19l0r2i" />

              <ul className="space-y-3" data-id="element-149" data-mp="bpdqmv55pntc">
                {[{
                label: t('Appels évités', 'Calls avoided'),
                value: '2,134'
              }, {
                label: t('Courriels évités', 'Emails avoided'),
                value: '847'
              }, {
                label: t('Visites au comptoir évitées', 'Counter visits avoided'),
                value: '319'
              }, {
                label: t('Heures de travail sauvées', 'Work hours saved'),
                value: '147 h'
              }].map(row => <li key={row.label} className="flex items-center justify-between gap-3" data-id="element-150" data-mp="ct4f1p3p2kiw">
                    <span className="text-sm text-slate-300" data-id="element-151" data-mp="qjptn1m488az">{row.label}</span>
                    <span className="font-bold" data-id="element-152" data-mp="lloakgoow8zi">{row.value}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* Channel performance */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-153" data-mp="3q1h7yxkz8l36">
        <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-154" data-mp="7vkp3rxmwjb4">
          {t('Performance par canal', 'Performance by channel')}
        </h2>
        <div className="overflow-x-auto" data-id="element-155" data-mp="1j9pyx1kj3ned">
          <table className="w-full text-sm text-left" data-id="element-156" data-mp="1j8nar1elbz25">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-157" data-mp="1ealrafd95xq5">
              <tr data-id="element-158" data-mp="1ecpsgpjroimq">
                <th className="px-4 py-3 rounded-l-lg" data-id="element-159" data-mp="2w889tq65h3ar">{t('Canal', 'Channel')}</th>
                <th className="px-4 py-3" data-id="element-160" data-mp="2wacpf5devqqv">{t('Messages envoyés', 'Messages sent')}</th>
                <th className="px-4 py-3" data-id="element-161" data-mp="318mm5kesazel">{t("Taux d'ouverture", 'Open rate')}</th>
                <th className="px-4 py-3 rounded-r-lg" data-id="element-162" data-mp="2n8v7rikcvh02">
                  {t("Taux d'interaction", 'Interaction rate')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-163" data-mp="3fvzblk7pppa9">
              {channelRows.map(row => <tr key={row.channel} className={row.best ? 'bg-indigo-50/30' : ''} data-id="element-164" data-mp="3jwrhq4lafe2b">
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-165" data-mp="366xrbhu4wlqh">
                    <span className="flex items-center gap-2" data-id="element-166" data-mp="3b5ivttgf5rkq">
                      {row.channel === 'atna' ? t('Assistant ATNA', 'ATNA assistant') : CHANNEL_LABELS[row.channel][lang]}
                      {row.best && <span className="px-2 py-0.5 text-[10px] bg-[#4F46E5] text-white rounded-full font-bold" data-id="element-167" data-mp="7hqpmuzp3wr2">
                          {t('MEILLEUR', 'BEST')}
                        </span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-168" data-mp="7d70or1s6ulw">{row.sent}</td>
                  <td className="px-4 py-3" data-id="element-169" data-mp="2fn9yrnwcpd2">
                    <div className="flex items-center gap-2" data-id="element-170" data-mp="2elw6ffhxorn">
                      <span className="w-10" data-id="element-171" data-mp="3uj6bmuvnt2ud">{row.open} %</span>
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-172" data-mp="3ukr8ir5g4xve">
                        <div className="h-full bg-[#4F46E5]" style={{
                      width: `${row.open}%`
                    }} data-id="element-173" data-mp="3pq4wclrjj1a9" />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-174" data-mp="3av70hix6ntof">
                    <div className="flex items-center gap-2" data-id="element-175" data-mp="3ax1k8twpxfqx">
                      <span className="w-10" data-id="element-176" data-mp="36ueset01rutq">{row.interact} %</span>
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-177" data-mp="1mbkxk1fhwcmg">
                        <div className="h-full bg-emerald-500" style={{
                      width: `${row.interact}%`
                    }} data-id="element-178" data-mp="1qcmpdx731mee" />
                      </div>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Coverage & funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-179" data-mp="12lhaplcvp05o">
        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col" data-id="element-180" data-mp="17my9nbbsycjm">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-181" data-mp="1cjx4jw3ainiv">
            {t('Couverture géographique', 'Geographic coverage')}
          </h2>

          <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-id="element-182" data-mp="jxccsuiwk9es">
            {sectors.map(s => <button key={s.id} onClick={() => setSelectedSector(s.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors border ${selectedSector === s.id ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-183" data-mp="ovdx1is2eeru">
                <span className="flex items-center gap-2" data-id="element-184" data-mp="3ie9gt7rlh1ku">
                  <span className={`w-2 h-2 rounded-full ${s.status === 'green' ? 'bg-emerald-500' : s.status === 'yellow' ? 'bg-amber-400' : 'bg-red-500'}`} data-id="element-185" data-mp="16fdcrg13doj" />
                  {sectorName(s.id)}
                </span>
              </button>)}
          </div>

          <div className="flex-1 bg-slate-50 rounded-xl border border-slate-100 p-5 flex flex-col justify-center" data-id="element-186" data-mp="jvlyel4aohdi">
            <div className="flex items-center gap-3 mb-4 flex-wrap" data-id="element-187" data-mp="ykx3wi5sh200">
              <MapPinIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-188" data-mp="yikoabni73q4" />
              <h3 className="text-xl font-bold text-[#0F172A]" data-id="element-189" data-mp="13ip8abnmegl3">
                {sectorName(active.id)}
              </h3>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${active.status === 'green' ? 'bg-emerald-100 text-emerald-700' : active.status === 'yellow' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-id="element-190" data-mp="om7wxkffl1yj">
                {active.status === 'green' ? t('Forte portée', 'Strong reach') : active.status === 'yellow' ? t('Portée moyenne', 'Average reach') : t('Faible portée', 'Low reach')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-191" data-mp="vlpnvd93lpl">
              {[{
              label: t('Citoyens inscrits', 'Registered citizens'),
              value: active.citizens.toLocaleString(locale)
            }, {
              label: t('Cartes bciti+ actives', 'Active bciti+ cards'),
              value: active.cards.toLocaleString(locale)
            }, {
              label: t("Taux d'ouverture moy.", 'Avg. open rate'),
              value: `${active.openRate} %`
            }, {
              label: t('Avis envoyés', 'Notices sent'),
              value: active.notices.toLocaleString(locale)
            }].map(item => <div key={item.label} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm" data-id="element-192" data-mp="3hojrjc67g9m1">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-193" data-mp="3mla77ye3l36x">{item.label}</div>
                  <div className="font-bold text-lg text-[#0F172A]" data-id="element-194" data-mp="3qoygclido6ua">{item.value}</div>
                </div>)}
              <div className="col-span-2 bg-indigo-50 p-3 rounded-lg border border-indigo-100 shadow-sm flex justify-between items-center gap-3" data-id="element-195" data-mp="1ez0etmmjvzb6">
                <div className="text-sm font-medium text-indigo-900" data-id="element-196" data-mp="161pznq6m3q0z">
                  {t("Questions posées à l'IA", 'Questions asked to the AI')}
                </div>
                <div className="font-bold text-xl text-[#4F46E5]" data-id="element-197" data-mp="1yp2ne3oie3vv">
                  {active.questions.toLocaleString(locale)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-198" data-mp="1oyr6lgjq807k">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-199" data-mp="1k0y530tdbudm">
            {t('Entonnoir de communication', 'Communication funnel')}
          </h2>
          <div className="space-y-2" data-id="element-200" data-mp="2hhwqo0crs7k9">
            {funnel.map(step => <div key={step.label} data-id="element-201" data-mp="2cniwg19ug6h6">
                <div className="flex items-center justify-between mb-1 px-1 gap-3" data-id="element-202" data-mp="arjove3xscex">
                  <span className="text-sm font-medium text-slate-700" data-id="element-203" data-mp="atxirjmvrikw">
                    {step.label}
                  </span>
                  <span className="text-sm font-bold text-slate-900" data-id="element-204" data-mp="129x5zd08vrc">
                    {step.value}
                  </span>
                </div>
                <div className="w-full h-8 bg-slate-100 rounded-lg overflow-hidden flex items-center" data-id="element-205" data-mp="5z0dmnsq1tda">
                  <div className={`h-full ${step.color} flex items-center justify-end px-3 transition-all duration-1000`} style={{
                width: `${step.percent}%`
              }} data-id="element-206" data-mp="3n7nc57tvwibw">
                    <span className="text-xs font-bold text-white drop-shadow-md" data-id="element-207" data-mp="3eamoj3en2o9i">
                      {step.percent} %
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </section>
      </div>

      {/* Topics & questions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-208" data-mp="1rjydghsm3kmu">
        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-209" data-mp="1rkgfjv1ygt2o">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-6" data-id="element-210" data-mp="1mm55pkbnuikg">
            {t('Sujets les plus engageants', 'Most engaging topics')}
          </h2>
          <div className="space-y-4" data-id="element-211" data-mp="1vl0f15a048rz">
            {topics.map((item, i) => <div key={item.topic} className="flex items-center justify-between gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-100" data-id="element-212" data-mp="2aag3ufpz2451" data-mpi={i}>
                <div className="flex items-center gap-3 min-w-0" data-id="element-213" data-mp="2ja3613qizdz5">
                  <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-214" data-mp="2f9j6jtihby9u">
                    {i + 1}
                  </div>
                  <div className="min-w-0" data-id="element-215" data-mp="kvrqoc9k4i5y">
                    <div className="font-medium text-slate-900 truncate" data-id="element-216" data-mp="kwtcnbs42p2g">
                      {item.topic}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-217" data-mp="mf03ixgphc3w">
                      {item.sent} {t('avis envoyés', 'notices sent')}
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 text-right flex-shrink-0" data-id="element-218" data-mp="vbiwuasun210">
                  <div data-id="element-219" data-mp="10bwv514nexlu">
                    <div className="text-xs text-slate-500" data-id="element-220" data-mp="10a2kcfn3clvh">
                      {t('Ouverture', 'Open')}
                    </div>
                    <div className="font-medium text-[#0F172A]" data-id="element-221" data-mp="7n9btb3gmiax">{item.open}</div>
                  </div>
                  <div data-id="element-222" data-mp="cmlzfy25ntek">
                    <div className="text-xs text-slate-500" data-id="element-223" data-mp="cm2k8p3w3j48">{t('Clics', 'Clicks')}</div>
                    <div className="font-medium text-[#0F172A]" data-id="element-224" data-mp="hem423t67ahh">{item.click}</div>
                  </div>
                  <div className="bg-indigo-50 px-2 py-1 rounded text-center min-w-[3rem]" data-id="element-225" data-mp="3g2t5ucbyg4r1">
                    <div className="text-[10px] text-[#4F46E5] font-bold uppercase" data-id="element-226" data-mp="3l1m9ze5cqjej">
                      {t('Questions', 'Questions')}
                    </div>
                    <div className="font-bold text-[#4F46E5]" data-id="element-227" data-mp="38kbfkufv0bbd">{item.q}</div>
                  </div>
                </div>
              </div>)}
          </div>
        </section>

        <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col" data-id="element-228" data-mp="33mtcvlsfqsbt">
          <div className="flex items-center gap-2 mb-6" data-id="element-229" data-mp="2ukad5ajy4vts">
            <MessageSquareIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-230" data-mp="2ulva16tqgqut" />
            <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-231" data-mp="2ptahs9qh6lte">
              {t('Insights ATNA : questions fréquentes', 'ATNA insights: frequent questions')}
            </h2>
          </div>

          <div className="bg-slate-50 rounded-xl p-1 flex-1" data-id="element-232" data-mp="2ftbaeg5sl1i4">
            <ul className="space-y-1" data-id="element-233" data-mp="2bsrflj8oy9z5">
              {questions.map((q, i) => <li key={q} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm" data-id="element-234" data-mp="26ug1uxf9zfbn" data-mpi={i}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i < 3 ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-235" data-mp="1x96rozu8hutg">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-slate-700" data-id="element-236" data-mp="2fxtwdh8i0i6i">{q}</span>
                  {i < 3 && <TrendingUpIcon className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" data-id="element-237" data-mp="2kwy2u041g1t5" />}
                </li>)}
            </ul>
          </div>
        </section>
      </div>
    </div>;
}